/** Pure helpers for the Activity page. Dates are YYYY-MM-DD strings. */
export type Period = 'day' | 'week' | 'month' | 'year' | 'custom'
export type Bucket = 'day' | 'week' | 'month'

export interface PeriodRange {
  from: string
  to: string
  bucket: Bucket
  compareFrom: string
  compareTo: string
  compareLabel: string
  /** The range the two charts draw; the same as from/to for a year or a custom range. */
  trendFrom: string
  trendTo: string
  trendBucket: Bucket
}

const DAY = 86_400_000
const parse = (s: string): Date => {
  const [y, m, d] = s.split('-').map(Number)
  return new Date(Date.UTC(y, m - 1, d))
}
const iso = (d: Date): string => d.toISOString().slice(0, 10)
const addDays = (s: string, n: number): string => iso(new Date(parse(s).getTime() + n * DAY))
const daysBetween = (a: string, b: string): number => Math.round((parse(b).getTime() - parse(a).getTime()) / DAY)
const monthStart = (y: number, m0: number): string => iso(new Date(Date.UTC(y, m0, 1)))
const monthEnd = (y: number, m0: number): string => iso(new Date(Date.UTC(y, m0 + 1, 0)))

/** Today's date in America/New_York, whatever zone the browser is in. */
export function etToday(now: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit' }).format(now)
}

export function periodRange(period: Period, today: string, custom?: { from: string; to: string }): PeriodRange {
  const t = parse(today)
  const y = t.getUTCFullYear()
  const m = t.getUTCMonth()
  if (period === 'day') {
    const prev = addDays(today, -1)
    return { from: today, to: today, bucket: 'day', compareFrom: prev, compareTo: prev, compareLabel: 'yesterday',
      trendFrom: addDays(today, -13), trendTo: today, trendBucket: 'day' }
  }
  // Ruling R13: a week or month so far is compared with the same days of the
  // one before, never with a whole finished period.
  if (period === 'week') {
    const monday = addDays(today, -((t.getUTCDay() + 6) % 7))
    const sunday = addDays(monday, 6)
    return { from: monday, to: today, bucket: 'day', compareFrom: addDays(monday, -7), compareTo: addDays(today, -7), compareLabel: 'the same days last week',
      trendFrom: addDays(monday, -77), trendTo: sunday, trendBucket: 'week' }
  }
  if (period === 'month') {
    // The 1st to the same day number last month, clamped to that month's length (Mar 31 → Feb 28).
    const lastLen = parse(monthEnd(y, m - 1)).getUTCDate()
    const sameDay = iso(new Date(Date.UTC(y, m - 1, Math.min(t.getUTCDate(), lastLen))))
    return { from: monthStart(y, m), to: today, bucket: 'day', compareFrom: monthStart(y, m - 1), compareTo: sameDay, compareLabel: 'the same days last month',
      trendFrom: monthStart(y, m - 11), trendTo: monthEnd(y, m), trendBucket: 'month' }
  }
  if (period === 'year') {
    const from = `${y}-01-01`
    // Feb 29 has no twin in the year before: use Feb 28.
    const sameDay = today.endsWith('-02-29') ? `${y - 1}-02-28` : `${y - 1}${today.slice(4)}`
    return { from, to: today, bucket: 'month', compareFrom: `${y - 1}-01-01`, compareTo: sameDay, compareLabel: 'the same dates last year',
      trendFrom: from, trendTo: today, trendBucket: 'month' }
  }
  const c = custom ?? { from: today, to: today }
  const n = daysBetween(c.from, c.to) + 1
  const bucket: Bucket = n > 62 ? 'week' : 'day'
  return { from: c.from, to: c.to, bucket, compareFrom: addDays(c.from, -n), compareTo: addDays(c.from, -1), compareLabel: `the ${n} days before`,
    trendFrom: c.from, trendTo: c.to, trendBucket: bucket }
}

const group = (n: number): string => Math.abs(n).toLocaleString('en-US')

/** The number change and the percentage: {number:'+14', pct:'(+9%)'}. `money` takes cents and shows whole dollars. */
export function change(cur: number, prev: number, money = false): { number: string; pct: string } {
  const d = money ? Math.round(cur / 100) - Math.round(prev / 100) : cur - prev
  const sign = d > 0 ? '+' : d < 0 ? '−' : '±'
  const number = `${sign}${money ? '$' : ''}${group(d)}`
  if (!prev) return { number, pct: '' }
  // Math.abs: a negative previous value (net change, say) must not flip the sign.
  const p = Math.round(((cur - prev) / Math.abs(prev)) * 100)
  return { number, pct: `(${p > 0 ? '+' : p < 0 ? '−' : ''}${Math.abs(p)}%)` }
}

/** About twelve axis labels: show every nth. */
export const labelStride = (n: number): number => Math.max(1, Math.ceil(n / 12))

/** Pixel heights scaled to the tallest value; a non-zero value is never shorter than 2px. */
export function barHeights(values: number[], maxPx: number): number[] {
  const max = Math.max(0, ...values)
  if (max === 0) return values.map(() => 0)
  return values.map((v) => (v <= 0 ? 0 : Math.max(2, Math.round((v / max) * maxPx))))
}

export const netChange = (t: { new: number; renewed: number; lapsed: number; cancelled: number }): number =>
  t.new + t.renewed - t.lapsed - t.cancelled

export const fmtCount = (n: number): string => n.toLocaleString('en-US')
export const fmtSigned = (n: number): string => (n > 0 ? '+' : n < 0 ? '−' : '') + group(n)
export const fmtMoney = (cents: number): string => `${cents < 0 ? '−' : ''}$${Math.round(Math.abs(cents) / 100).toLocaleString('en-US')}`
export const fmtMoneyExact = (cents: number): string =>
  `${cents < 0 ? '−' : ''}$${(Math.abs(cents) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
export function fmtShortMoney(cents: number): string {
  const d = Math.round(cents / 100)
  return d >= 1000 ? `$${(d / 1000).toFixed(d >= 10000 ? 0 : 1)}k` : `$${d}`
}

const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
/** "Oct 4" for day and week buckets, "Oct ’26"/"Oct" for months. */
export function bucketLabel(start: string, bucket: Bucket, withYear = false): string {
  const [y, m, d] = start.split('-').map(Number)
  if (bucket === 'month') return withYear ? `${MON[m - 1]} ’${String(y).slice(2)}` : MON[m - 1]
  return `${MON[m - 1]} ${d}`
}
export const fmtDay = (s: string): string => {
  const [y, m, d] = s.split('-').map(Number)
  return `${MON[m - 1]} ${d}, ${y}`
}

export type QuickPick = '7d' | '30d' | 'quarter' | 'year'
/** The custom range's quick picks: rolling 7 and 30 days, the last whole quarter, the last whole year. */
export function quickRange(pick: QuickPick, today: string): { from: string; to: string } {
  const t = parse(today)
  const y = t.getUTCFullYear()
  if (pick === '7d') return { from: addDays(today, -6), to: today }
  if (pick === '30d') return { from: addDays(today, -29), to: today }
  if (pick === 'year') return { from: `${y - 1}-01-01`, to: `${y - 1}-12-31` }
  const q0 = Math.floor(t.getUTCMonth() / 3) * 3 - 3
  return { from: monthStart(y, q0), to: monthEnd(y, q0 + 2) }
}
